import { useState, useMemo, useCallback } from 'react';

export type SortDirection = 'asc' | 'desc';

// Search (fleet filter contract v3 DRAFT, §16.5): case- AND accent-insensitive text matching,
// plus a digits-only fallback so a query like "2395550111" finds a field displaying
// "(239) 555-0111". Both are additive over the plain substring match -- neither can turn a
// non-match into a false positive, since digitsOnly('') never matches a non-empty query and
// foldText is a strict generalization of toLowerCase (never folds two DIFFERENT letters together).

/** Lowercase with diacritics stripped (NFD decompose + drop combining marks) -- "José" -> "jose". */
function foldText(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/** Strip everything but digits -- "(239) 555-0111" -> "2395550111". */
function digitsOnly(s: string): string {
  return s.replace(/\D/g, '');
}

function textMatches(value: string, query: string): boolean {
  return foldText(value).includes(foldText(query));
}

function phoneMatches(value: string, query: string): boolean {
  const q = digitsOnly(query);
  if (!q) return false;
  return digitsOnly(value).includes(q);
}

export interface MatchRange {
  start: number;
  end: number;
}

/**
 * The first highlightable span in `value` for `query`, or null if none exists. Used for
 * highlighting (§16.5: "the highlight marks the first match in each matched field"), not for the
 * filter decision itself -- `textMatches`/`phoneMatches` above decide whether a row is INCLUDED;
 * this only decides what to underline once it already is.
 *
 * Plain case-insensitive substring first (covers the overwhelming majority of real searches with
 * an exact, reliably-indexable span). Accent-folded matches are deliberately NOT highlighted here:
 * NFD decomposition can change which code units correspond to which, so a folded match's start/end
 * can't always be mapped back to a contiguous slice of the ORIGINAL string -- returning a wrong
 * span would be worse than returning none. A digits-only (phone) match CAN be mapped back exactly
 * (each kept digit's original index is recorded while stripping), so that path is supported.
 */
export function findFirstMatch(value: string, query: string): MatchRange | null {
  if (!query) return null;

  const lowerValue = value.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const idx = lowerValue.indexOf(lowerQuery);
  if (idx !== -1) return { start: idx, end: idx + query.length };

  if (/\d/.test(query)) {
    const qDigits = digitsOnly(query);
    if (qDigits) {
      const digitIndices: number[] = [];
      let digitsStr = '';
      for (let i = 0; i < value.length; i++) {
        if (/\d/.test(value[i])) {
          digitsStr += value[i];
          digitIndices.push(i);
        }
      }
      const dIdx = digitsStr.indexOf(qDigits);
      if (dIdx !== -1) {
        return { start: digitIndices[dIdx], end: digitIndices[dIdx + qDigits.length - 1] + 1 };
      }
    }
  }

  return null;
}

export interface SortConfig<T> {
  column: keyof T | string;
  direction: SortDirection;
}

export interface ColumnConfig<T> {
  key: keyof T | string;
  getValue?: (item: T) => string | number | Date | boolean | null | undefined;
  sortable?: boolean;
}

export interface UseTableStateOptions<T> {
  searchKeys?: (keyof T)[];
  initialSort?: SortConfig<T>;
  columns?: ColumnConfig<T>[];
  /**
   * Controlled sort. When `onSortChange` is provided, sort is driven by this value (e.g. from the
   * URL) instead of internal state, and clicking a header calls `onSortChange` with the next sort
   * (cycling asc → desc → null). `sort` null = no sort. Leave both undefined for internal sort.
   */
  sort?: SortConfig<T> | null;
  onSortChange?: (next: SortConfig<T> | null) => void;
  /** Controlled search text (paired with the parent owning the search box). */
  searchTextValue?: string;
  onSearchTextChange?: (text: string) => void;
}

/** Pure asc → desc → null cycle used by both internal and controlled sort. */
function computeNextSort<T>(
  current: SortConfig<T> | null,
  column: keyof T | string
): SortConfig<T> | null {
  if (!current || current.column !== column) return { column, direction: 'asc' };
  if (current.direction === 'asc') return { column, direction: 'desc' };
  return null; // desc → cleared
}

export interface UseTableStateReturn<T> {
  sortColumn: keyof T | string | null;
  sortDirection: SortDirection | null;
  filters: Record<string, string>;
  searchText: string;
  setSort: (column: keyof T | string) => void;
  setFilter: (column: string, value: string) => void;
  setSearchText: (text: string) => void;
  clearFilters: () => void;
  clearSort: () => void;
  processedData: T[];
}

export function useTableState<T extends object>(
  data: T[],
  options: UseTableStateOptions<T> = {}
): UseTableStateReturn<T> {
  const { searchKeys = [], initialSort, columns = [], sort, onSortChange, searchTextValue, onSearchTextChange } = options;

  const sortControlled = onSortChange !== undefined;
  const searchControlled = onSearchTextChange !== undefined;

  const [internalSortColumn, setInternalSortColumn] = useState<keyof T | string | null>(
    initialSort?.column ?? null
  );
  const [internalSortDirection, setInternalSortDirection] = useState<SortDirection | null>(
    initialSort?.direction ?? null
  );
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [internalSearchText, setInternalSearchText] = useState('');

  // Effective sort/search: controlled value when the parent owns it, else internal state.
  const sortColumn = sortControlled ? (sort ? sort.column : null) : internalSortColumn;
  const sortDirection = sortControlled ? (sort ? sort.direction : null) : internalSortDirection;
  const searchText = searchControlled ? (searchTextValue ?? '') : internalSearchText;

  const setSort = useCallback((column: keyof T | string) => {
    if (sortControlled) {
      const current = sort ?? null;
      onSortChange!(computeNextSort(current, column));
      return;
    }
    setInternalSortColumn(prevColumn => {
      if (prevColumn !== column) {
        setInternalSortDirection('asc');
        return column;
      }

      setInternalSortDirection(prevDir => {
        if (prevDir === 'asc') return 'desc';
        if (prevDir === 'desc') return null;
        return 'asc';
      });

      return prevColumn;
    });
  }, [sortControlled, sort, onSortChange]);

  const setSearchText = useCallback((text: string) => {
    if (searchControlled) onSearchTextChange!(text);
    else setInternalSearchText(text);
  }, [searchControlled, onSearchTextChange]);

  const setFilter = useCallback((column: string, value: string) => {
    setFilters(prev => {
      if (!value) {
        const { [column]: _removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [column]: value };
    });
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({});
    setSearchText('');
  }, [setSearchText]);

  const clearSort = useCallback(() => {
    if (sortControlled) onSortChange!(null);
    else {
      setInternalSortColumn(null);
      setInternalSortDirection(null);
    }
  }, [sortControlled, onSortChange]);

  const processedData = useMemo(() => {
    let result = [...data];

    // Apply search filter -- case/accent-insensitive, plus a digits-only fallback for phone-style
    // fields (§16.5).
    if (searchText && searchKeys.length > 0) {
      result = result.filter(item =>
        searchKeys.some(key => {
          const value = (item as Record<string, unknown>)[key as string];
          if (value == null) return false;
          const s = String(value);
          return textMatches(s, searchText) || phoneMatches(s, searchText);
        })
      );
    }

    // Apply column filters
    Object.entries(filters).forEach(([column, filterValue]) => {
      if (!filterValue) return;
      const lowerFilter = filterValue.toLowerCase();
      result = result.filter(item => {
        const value = (item as Record<string, unknown>)[column];
        if (value == null) return false;
        return String(value).toLowerCase().includes(lowerFilter);
      });
    });

    // Apply sorting
    if (sortColumn && sortDirection) {
      const columnConfig = columns.find(c => c.key === sortColumn);

      result.sort((a, b) => {
        let aValue: unknown;
        let bValue: unknown;

        // Use custom getValue if provided
        if (columnConfig?.getValue) {
          aValue = columnConfig.getValue(a);
          bValue = columnConfig.getValue(b);
        } else {
          aValue = (a as Record<string, unknown>)[sortColumn as string];
          bValue = (b as Record<string, unknown>)[sortColumn as string];
        }

        // Handle null/undefined
        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return sortDirection === 'asc' ? 1 : -1;
        if (bValue == null) return sortDirection === 'asc' ? -1 : 1;

        // Compare values
        let comparison = 0;

        if (typeof aValue === 'number' && typeof bValue === 'number') {
          comparison = aValue - bValue;
        } else if (aValue instanceof Date && bValue instanceof Date) {
          comparison = aValue.getTime() - bValue.getTime();
        } else if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
          comparison = aValue === bValue ? 0 : aValue ? -1 : 1;
        } else {
          // String comparison
          comparison = String(aValue).localeCompare(String(bValue), undefined, {
            numeric: true,
            sensitivity: 'base',
          });
        }

        return sortDirection === 'asc' ? comparison : -comparison;
      });
    }

    return result;
  }, [data, searchText, searchKeys, filters, sortColumn, sortDirection, columns]);

  return {
    sortColumn,
    sortDirection,
    filters,
    searchText,
    setSort,
    setFilter,
    setSearchText: setSearchText,
    clearFilters,
    clearSort,
    processedData,
  };
}

export default useTableState;
